// Cardio targets in plain words, for the workout and exercise pages, the session player and the coach:
// "6 x 800 m at 4:30 min/km, 2 min easy jog", "30 min easy in zone 2", "8 x 30 s uphill at effort 9 of 10".
import type { Amount, Block, CardioSport, CardioTarget, Section } from '../types';
import { CARDIO_SPORTS } from './sports';
import { formatDistance, formatDuration, formatSportSpeed, speedToPace } from './units';

const RECOVERY_WORDS: Record<NonNullable<CardioTarget['recovery']>, string> = {
  jog: 'easy jog', walk: 'walk', rest: 'rest', easy_spin: 'easy spin', easy_row: 'easy rowing', easy_swim: 'easy swimming',
};

export const STYLE_LABELS: Record<NonNullable<Section['cardio']>['style'], string> = {
  steady: 'Steady', intervals: 'Intervals', tempo: 'Tempo', fartlek: 'Fartlek', long: 'Long and easy', hill_repeats: 'Hill repeats', drills: 'Drills',
};

/** One cardio block in words: "800 m at 4:30 min/km", "2 min easy jog", "30 min easy in zone 2". */
export function describeTarget(t: CardioTarget, sport?: CardioSport, amount?: Amount): string {
  const meters = sport ? CARDIO_SPORTS[sport].meters : false;
  const measure = sport ? CARDIO_SPORTS[sport].measure : 'pace_km';
  const seconds = t.durationSec ?? (t.distanceM === undefined && amount?.kind === 'time' ? amount.seconds : undefined);
  const size = t.distanceM !== undefined ? formatDistance(t.distanceM, { meters }) : seconds !== undefined ? formatDuration(seconds) : '';
  const parts: string[] = [];
  if (t.role === 'recovery') {
    parts.push(size, RECOVERY_WORDS[t.recovery ?? 'jog']);
  } else {
    parts.push(size);
    if (t.role === 'easy') parts.push('easy');
    if (t.elevationM) parts.push(`uphill (about ${formatDistance(t.elevationM)} climb)`);
  }
  if (t.paceSecPerKm !== undefined) {
    const pace = formatSportSpeed(t.paceSecPerKm, measure);
    parts.push(t.paceMaxSecPerKm !== undefined && t.paceMaxSecPerKm !== t.paceSecPerKm
      ? `at ${pace.replace(/ .*/, '')} to ${formatSportSpeed(t.paceMaxSecPerKm, measure)}`
      : `at ${pace}`);
  } else if (t.speedKmh !== undefined) {
    parts.push(`at ${formatSportSpeed(speedToPace(t.speedKmh), measure)}`);
  }
  if (t.zone !== undefined) parts.push(`in zone ${t.zone}`);
  if (t.rpe !== undefined) parts.push(`at effort ${t.rpe} of 10`);
  if (t.cadence !== undefined) parts.push(`cadence ${t.cadence}`);
  return parts.filter(Boolean).join(' ');
}

/** The sport a section is in: its own, else the workout's. */
const sportOf = (section: Section, workoutSport?: CardioSport) => section.cardio?.sport ?? workoutSport;

/**
 * A whole cardio section in one line: intervals as "6 x 800 m at 4:30 min/km, 2 min easy jog", a steady or
 * long section as its blocks joined with "then". Null when the section has no cardio targets.
 */
export function describeCardioSection(section: Section, workoutSport?: CardioSport): string | null {
  const blocks = section.blocks.filter((b): b is Block & { cardio: CardioTarget } => !!b.cardio);
  if (blocks.length === 0) return null;
  const sport = sportOf(section, workoutSport);
  const words = blocks.map((b) => describeTarget(b.cardio, sport, b.amount ?? (section.format.kind === 'circuit' ? { kind: 'time', seconds: section.format.workSec } : undefined)));
  if (section.format.kind === 'circuit') {
    // A rest in the format (pool lengths) when no recovery block is written out.
    const rest = section.format.restSec > 0 && !blocks.some((b) => b.cardio.role === 'recovery') ? [`${formatDuration(section.format.restSec)} rest`] : [];
    const line = [...words, ...rest].join(', ');
    return section.format.rounds > 1 ? `${section.format.rounds} x ${line}` : line;
  }
  return words.join(', then ');
}
